/* ==========================================================================
   Scirocco — product.js (area T3: scheda prodotto)
   Usa le API di theme.js (window.SC): SC.cart, SC.dialog, SC.toast, SC.track, SC.motion.

   Custom element:
     <sc-product-form>   intercetta l'invio del form, stati del bottone, volo verso il carrello
     <sc-gallery>        pallini sincronizzati con lo scorrimento, click → lightbox
     <sc-pair-picker>  scelta del 2° paio, totali, "Aggiungi entrambi"
     <sc-delivery-eta>   "Ordina entro 3 h 12 min: spedito oggi, arriva giovedì 21" (ora di Roma)
   Più: barra fissa in basso, form "Avvisami quando torna", iframe della prova virtuale
   (creato all'apertura, rimosso alla chiusura: libera la fotocamera) e lightbox della galleria.
   Tutto opzionale: se un elemento non c'è, la funzione non fa nulla.
   ========================================================================== */
(() => {
  'use strict';

  const SC = window.SC;
  if (!SC) return;

  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const reduced = () => (SC.motion && SC.motion.reduced ? SC.motion.reduced() : window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  const strings = () => (SC.config && SC.config.strings) || {};
  const setts = () => (SC.config && SC.config.settings) || {};

  const escapeHtml = (s) =>
    String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

  /** Sostituisce i segnaposto [x] di un modello di frase preso da un data-attribute. */
  const tpl = (template, values) => {
    let out = String(template || '');
    Object.keys(values).forEach((key) => {
      out = out.split(key).join(values[key]);
    });
    return out;
  };

  const tick = (el) => {
    if (!el) return;
    el.classList.remove('is-tick');
    void el.offsetWidth;
    el.classList.add('is-tick');
  };

  const cartTarget = () => $$('[data-sc-cart-icon]').find((el) => el.getBoundingClientRect().width > 0) || null;

  /** Aggiunta al carrello condivisa: niente drawer prima del volo della miniatura. */
  async function addToCart(items, { source, flyFrom } = {}) {
    const pageMode = setts().cartType === 'page';
    const data = await SC.cart.add(items, { source: source || 'pdp', open: pageMode ? undefined : false, toast: false });
    if (pageMode) return data; // SC.cart.add ha già avviato la navigazione al carrello
    if (flyFrom && SC.motion && SC.motion.flyTo) {
      const target = cartTarget();
      if (target) {
        try {
          await SC.motion.flyTo(flyFrom, target);
        } catch (e) {
          /* animazione non disponibile: si va avanti */
        }
      }
    }
    if (!SC.dialog.open('cart')) {
      SC.toast(strings().toastAdded || '', {
        action: { label: strings().toastView || '', href: (SC.config.routes || {}).cart || '/cart', open: 'cart' },
      });
    }
    return data;
  }

  /** Etichetta dei bottoni "aggiungi": "Aggiungine un altro" se il prodotto è già nel carrello.
   *  La barra fissa usa la versione corta (senza prezzo: il prezzo ce l'ha già accanto). */
  function setAtcLabel(inCart) {
    const form = $('sc-product-form');
    if (!form) return;
    const label = inCart ? form.dataset.labelAgain : form.dataset.labelFirst;
    const short = inCart ? form.dataset.labelShortAgain : form.dataset.labelShortFirst;
    if (label) {
      $$('[data-sc-atc-label]').forEach((el) => {
        if (el.innerHTML !== label) el.innerHTML = label;
      });
    }
    if (short) {
      $$('[data-sc-atc-label-short]').forEach((el) => {
        if (el.innerHTML !== short) el.innerHTML = short;
      });
    }
  }
  function markAdded() {
    setAtcLabel(true);
  }

  /** Carrello attuale, letto UNA volta per aggiornamento (usa la risposta di change/update se c'è). */
  let cartPeek = null;
  function peekCart(event) {
    if (cartPeek && cartPeek.event === event) return cartPeek.promise;
    const detail = (event && event.detail) || {};
    const promise =
      detail.cart && Array.isArray(detail.cart.items)
        ? Promise.resolve(detail.cart)
        : SC.cart.get().catch(() => null);
    cartPeek = { event, promise };
    return promise;
  }

  // se il prodotto esce dal carrello l'etichetta torna "Aggiungi al carrello — 59 €"
  document.addEventListener('sc:cart:updated', async (event) => {
    const pdp = $('[data-sc-pdp]');
    if (!pdp || !$('sc-product-form')) return;
    const cart = await peekCart(event);
    if (!cart) return;
    const id = Number(pdp.dataset.productId);
    setAtcLabel((cart.items || []).some((i) => Number(i.product_id) === id));
  });

  /* ---------- <sc-product-form> ---------- */
  class ProductForm extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.bound = true;
      this.form = this.querySelector('form');
      this.live = this.querySelector('[data-sc-atc-live]');
      if (!this.form) return;
      this.form.addEventListener('submit', (e) => this.onSubmit(e));
    }

    buttons() {
      const id = this.form.id;
      return $$('[data-sc-atc]').filter((b) => this.contains(b) || b.getAttribute('form') === id);
    }

    state(value) {
      this.buttons().forEach((b) => {
        b.dataset.state = value;
      });
    }

    async onSubmit(event) {
      event.preventDefault();
      if (this.busy) return;
      const input = this.form.querySelector('[name="id"]');
      const id = Number(input && input.value);
      if (!id) {
        this.form.submit();
        return;
      }
      this.busy = true;
      this.state('loading');
      if (this.live) this.live.textContent = this.dataset.adding || '';
      const gallery = $('sc-gallery');
      const img = gallery && gallery.activeImage ? gallery.activeImage() : null;
      try {
        await addToCart([{ id, quantity: 1 }], { source: 'pdp', flyFrom: img });
        this.state('done');
        if (this.live) this.live.textContent = this.dataset.added || '';
        setTimeout(() => {
          this.state('idle');
          markAdded();
        }, 1400);
      } catch (err) {
        this.state('error');
        // l'errore (es. scorte finite) va letto anche dagli screen reader, non solo nel toast
        if (this.live) this.live.textContent = (err && err.message) || strings().cartError || '';
        setTimeout(() => this.state('idle'), 800);
      } finally {
        this.busy = false;
      }
    }
  }
  SC.define('sc-product-form', ProductForm);

  /* ---------- <sc-gallery> ---------- */
  class Gallery extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.bound = true;
      this.track = this.querySelector('[data-sc-gallery-track]');
      this.slides = $$('.sc-gallery__slide', this);
      this.dots = $$('[data-sc-dot]', this);
      this.index = 0;
      if (!this.track) return;

      this.dots.forEach((dot) => {
        dot.addEventListener('click', () => this.goTo(Number(dot.dataset.scDot)));
      });

      this.addEventListener('click', (e) => {
        const zoom = e.target.closest('[data-sc-zoom]');
        if (zoom) openLightbox(Number(zoom.dataset.scZoom), zoom);
      });

      let raf = 0;
      this.track.addEventListener(
        'scroll',
        () => {
          cancelAnimationFrame(raf);
          raf = requestAnimationFrame(() => {
            const w = this.track.clientWidth || 1;
            this.setActive(Math.round(this.track.scrollLeft / w));
          });
        },
        { passive: true }
      );
    }

    setActive(i) {
      if (i < 0 || i >= this.slides.length || i === this.index) return;
      this.index = i;
      this.dots.forEach((dot, n) => {
        if (n === i) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
    }

    goTo(i) {
      const slide = this.slides[i];
      if (!slide) return;
      this.track.scrollTo({ left: i * (this.track.clientWidth || 0), behavior: reduced() ? 'auto' : 'smooth' });
      this.setActive(i);
    }

    images() {
      return this.slides.map((s) => s.querySelector('img')).filter(Boolean);
    }

    activeImage() {
      const slide = this.slides[this.index] || this.slides[0];
      return slide ? slide.querySelector('img') : null;
    }
  }
  SC.define('sc-gallery', Gallery);

  /* ---------- Lightbox ---------- */
  const lightbox = () => document.getElementById('sc-dialog-lightbox');

  function lightboxTrack() {
    const dlg = lightbox();
    return dlg ? dlg.querySelector('[data-sc-lb-track]') : null;
  }

  function buildLightbox() {
    const track = lightboxTrack();
    const gallery = $('sc-gallery');
    if (!track || !gallery || track.children.length) return;
    gallery.slides.forEach((slide) => {
      const img = slide.querySelector('img');
      if (!img) return;
      const fig = document.createElement('figure');
      fig.className = 'sc-lightbox__slide';
      const clone = img.cloneNode(true);
      clone.removeAttribute('class');
      clone.removeAttribute('fetchpriority');
      clone.setAttribute('loading', 'lazy');
      clone.setAttribute('sizes', '100vw');
      fig.appendChild(clone);
      // foto indossate generate con AI: l'etichetta resta visibile anche a schermo intero
      const tag = slide.querySelector('.sc-ai-tag');
      if (tag) fig.appendChild(tag.cloneNode(true));
      track.appendChild(fig);
    });
  }

  function lightboxUpdate(index) {
    const dlg = lightbox();
    const track = lightboxTrack();
    if (!dlg || !track) return;
    const total = track.children.length;
    const i = Math.max(0, Math.min(index, total - 1));
    const count = dlg.querySelector('[data-sc-lb-count]');
    if (count) count.textContent = total ? `${i + 1} / ${total}` : '';
    const prev = dlg.querySelector('[data-sc-lb-prev]');
    const next = dlg.querySelector('[data-sc-lb-next]');
    if (prev) prev.disabled = i <= 0;
    if (next) next.disabled = i >= total - 1;
    dlg.dataset.index = String(i);
  }

  function lightboxGoTo(index, instant) {
    const track = lightboxTrack();
    if (!track) return;
    const total = track.children.length;
    const i = Math.max(0, Math.min(index, total - 1));
    track.scrollTo({ left: i * (track.clientWidth || 0), behavior: instant || reduced() ? 'auto' : 'smooth' });
    lightboxUpdate(i);
  }

  function openLightbox(index, trigger) {
    if (!lightbox()) return;
    buildLightbox();
    SC.dialog.open('lightbox', { trigger });
    requestAnimationFrame(() => lightboxGoTo(index, true));
  }

  function bindLightbox() {
    const dlg = lightbox();
    if (!dlg || dlg.dataset.bound === '1') return;
    dlg.dataset.bound = '1';
    const track = lightboxTrack();
    const prev = dlg.querySelector('[data-sc-lb-prev]');
    const next = dlg.querySelector('[data-sc-lb-next]');
    const current = () => Number(dlg.dataset.index || 0);
    if (prev) prev.addEventListener('click', () => lightboxGoTo(current() - 1));
    if (next) next.addEventListener('click', () => lightboxGoTo(current() + 1));
    if (track) {
      let raf = 0;
      track.addEventListener(
        'scroll',
        () => {
          cancelAnimationFrame(raf);
          raf = requestAnimationFrame(() => lightboxUpdate(Math.round(track.scrollLeft / (track.clientWidth || 1))));
        },
        { passive: true }
      );
    }
    dlg.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        lightboxGoTo(current() - 1);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        lightboxGoTo(current() + 1);
      }
    });
  }

  /* ---------- Prova virtuale: iframe solo mentre il pannello è aperto ---------- */
  let tryonTimer = 0;

  function mountTryon() {
    const dlg = document.getElementById('sc-dialog-tryon');
    if (!dlg) return;
    const frame = dlg.querySelector('[data-sc-tryon-frame]');
    if (!frame || frame.querySelector('iframe')) return;
    const base = String((setts().tryon || {}).base || '').trim();
    const id = dlg.dataset.tryonId;
    if (!base || !id) return;
    const iframe = document.createElement('iframe');
    iframe.src = base.replace(/\/*$/, '/') + id;
    iframe.title = dlg.dataset.tryonTitle || '';
    iframe.allow = 'camera; fullscreen';
    iframe.setAttribute('allowfullscreen', '');
    iframe.addEventListener('load', () => frame.classList.add('is-loaded'), { once: true });
    frame.appendChild(iframe);
    clearTimeout(tryonTimer);
    // rete di sicurezza: se "load" non arriva, dopo 8s mostriamo comunque l'app
    tryonTimer = setTimeout(() => frame.classList.add('is-loaded'), 8000);
  }

  function unmountTryon() {
    const dlg = document.getElementById('sc-dialog-tryon');
    if (!dlg) return;
    clearTimeout(tryonTimer);
    const frame = dlg.querySelector('[data-sc-tryon-frame]');
    if (!frame) return;
    frame.classList.remove('is-loaded');
    const iframe = frame.querySelector('iframe');
    if (iframe) {
      iframe.src = 'about:blank';
      iframe.remove();
    }
  }

  SC.on('sc:dialog:open', (e) => {
    if (e.detail && e.detail.id === 'tryon') mountTryon();
  });
  SC.on('sc:dialog:close', (e) => {
    if (e.detail && e.detail.id === 'tryon') unmountTryon();
  });

  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-sc-tryon-add]');
    if (!btn || btn.dataset.state === 'loading') return;
    const input = $('sc-product-form [name="id"]');
    const id = Number(input && input.value);
    if (!id) return;
    btn.dataset.state = 'loading';
    SC.dialog.close('tryon');
    try {
      await addToCart([{ id, quantity: 1 }], { source: 'tryon' });
      btn.dataset.state = 'idle';
      markAdded();
    } catch (err) {
      btn.dataset.state = 'error';
      setTimeout(() => {
        btn.dataset.state = 'idle';
      }, 800);
    }
  });

  /* ---------- <sc-pair-picker> ---------- */
  class BundlePicker extends HTMLElement {
    connectedCallback() {
      if (!this.onCart) this.onCart = (event) => this.syncMode(event);
      document.addEventListener('sc:cart:updated', this.onCart);
      if (this.bound) return;
      this.bound = true;
      this.items = $$('.sc-pair__item', this);
      this.addBtn = this.querySelector('[data-sc-pair-add]');
      this.totalEl = this.querySelector('[data-sc-pair-total]');
      this.fullEl = this.querySelector('[data-sc-pair-full]');
      this.btnTotal = this.querySelector('[data-sc-pair-btn-total]');
      this.secondTotalEls = $$('[data-sc-pair-second-total], [data-sc-pair-btn-second]', this);
      this.secondFullEl = this.querySelector('[data-sc-pair-second-full]');
      this.productId = Number(this.dataset.productId || 0);
      this.mode = this.dataset.mode === 'second' ? 'second' : 'both';
      this.off = Number(this.dataset.off || 0); // sconto sul 2° paio, in centesimi
      this.currentPrice = Number(this.dataset.currentPrice || 0);
      this.currentId = Number(this.dataset.currentId || 0);
      this.selected = this.items.find((i) => i.getAttribute('aria-pressed') === 'true') || this.items[0] || null;
      this.items.forEach((item) => item.addEventListener('click', () => this.select(item)));
      if (this.addBtn) this.addBtn.addEventListener('click', () => this.add());
      this.totals(true);
    }

    disconnectedCallback() {
      document.removeEventListener('sc:cart:updated', this.onCart);
    }

    /** Il prodotto è già nel carrello in attesa del suo compagno? Allora si aggiunge solo il secondo. */
    async syncMode(event) {
      const cart = await peekCart(event);
      if (!cart) return;
      const items = (cart && cart.items) || [];
      let n = 0;
      let inCart = false;
      items.forEach((i) => {
        if (!i.gift_card && i.original_price > 0) n += i.quantity;
        if (Number(i.product_id) === this.productId) inCart = true;
      });
      this.setMode(inCart && n % 2 === 1 ? 'second' : 'both');
    }

    setMode(mode) {
      if (mode === this.mode) return;
      this.mode = mode;
      this.dataset.mode = mode;
      $$('[data-sc-pair-when]', this).forEach((el) => {
        el.hidden = el.getAttribute('data-sc-pair-when') !== mode;
      });
    }

    select(item) {
      if (!item || item === this.selected) return;
      this.items.forEach((i) => i.setAttribute('aria-pressed', i === item ? 'true' : 'false'));
      this.selected = item;
      this.totals(false);
      if (SC.motion && SC.motion.bump) SC.motion.bump(item.querySelector('.sc-pair__img'));
    }

    totals(silent) {
      if (!this.selected) return;
      const price = Number(this.selected.dataset.price || 0);
      const hi = Math.max(price, this.currentPrice);
      const lo = Math.min(price, this.currentPrice);
      this.total = hi + Math.max(0, lo - this.off); // come il Liquid
      this.full = price + this.currentPrice;
      [this.totalEl, this.btnTotal].forEach((el) => {
        if (!el) return;
        el.textContent = SC.money(this.total);
        if (!silent) tick(el);
      });
      if (this.fullEl) this.fullEl.textContent = SC.money(this.full);
      // modalità "solo il secondo": quanto costa in più il paio scelto (la coppia meno questo, già nel carrello)
      this.secondTotal = this.total - this.currentPrice;
      this.secondTotalEls.forEach((el) => {
        el.textContent = SC.money(this.secondTotal);
        if (!silent) tick(el);
      });
      if (this.secondFullEl) this.secondFullEl.textContent = SC.money(price);
    }

    async add() {
      if (!this.selected || this.busy) return;
      const secondId = Number(this.selected.dataset.variantId);
      if (!this.currentId || !secondId) return;
      this.busy = true;
      const btn = this.addBtn;
      if (btn) btn.dataset.state = 'loading';
      const onlySecond = this.mode === 'second';
      const wanted = onlySecond ? [secondId] : [this.currentId, secondId];
      try {
        const data = await addToCart(
          wanted.map((id) => ({ id, quantity: 1 })),
          { source: 'bundle', flyFrom: this.selected.querySelector('img') }
        );
        const ids = ((data && data.items) || []).map((i) => Number(i.variant_id || i.id));
        const ok = wanted.every((id) => ids.indexOf(id) > -1);
        if (!ok) {
          SC.toast(this.dataset.error || strings().cartError || '', { variant: 'error' });
          if (btn) btn.dataset.state = 'error';
          setTimeout(() => {
            if (btn) btn.dataset.state = 'idle';
          }, 900);
        } else {
          if (btn) btn.dataset.state = 'done';
          SC.track('AddBundle', {
            content_ids: wanted.map(String),
            content_type: 'product',
            num_items: wanted.length,
            value: ((onlySecond ? this.secondTotal : this.total) || 0) / 100,
            currency: SC.config.currency,
            discount: this.off / 100,
            product: this.dataset.product || '',
          });
          setTimeout(() => {
            if (btn) btn.dataset.state = 'idle';
            markAdded();
          }, 1400);
        }
      } catch (err) {
        if (btn) btn.dataset.state = 'error';
        setTimeout(() => {
          if (btn) btn.dataset.state = 'idle';
        }, 800);
      } finally {
        this.busy = false;
      }
    }
  }
  SC.define('sc-pair-picker', BundlePicker);

  /* ---------- <sc-delivery-eta> ---------- */
  const DAY = 86400000;
  const TZ = 'Europe/Rome';

  function romeNow() {
    const opts = { timeZone: TZ, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' };
    let parts;
    try {
      parts = new Intl.DateTimeFormat('en-GB', Object.assign({ hourCycle: 'h23' }, opts)).formatToParts(new Date());
    } catch (e) {
      parts = new Intl.DateTimeFormat('en-GB', Object.assign({ hour12: false }, opts)).formatToParts(new Date());
    }
    const out = {};
    parts.forEach((p) => {
      if (p.type !== 'literal') out[p.type] = parseInt(p.value, 10);
    });
    if (out.hour === 24) out.hour = 0;
    return out;
  }

  /** Spedizioni e consegne lun-sab: le domeniche slittano al giorno dopo (parità con il tema v18). */
  // giorni lavorativi del corriere in Italia: lun-ven, escluse le festività nazionali (e Pasquetta)
  const HOLIDAYS = ['1-1', '1-6', '4-25', '5-1', '6-2', '8-15', '11-1', '12-8', '12-25', '12-26'];
  function easterMonday(y) {
    const a = y % 19;
    const b = Math.floor(y / 100);
    const c = y % 100;
    const d = Math.floor(b / 4);
    const e = b % 4;
    const f = Math.floor((b + 8) / 25);
    const g = Math.floor((b - f + 1) / 3);
    const h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4);
    const k = c % 4;
    const l = (32 + 2 * e + 2 * i - h - k) % 7;
    const m = Math.floor((a + 11 * h + 22 * l) / 451);
    const month = Math.floor((h + l - 7 * m + 114) / 31);
    const day = ((h + l - 7 * m + 114) % 31) + 1;
    return Date.UTC(y, month - 1, day) + DAY;
  }
  function isWorkday(d) {
    const wd = d.getUTCDay();
    if (wd === 0 || wd === 6) return false;
    if (HOLIDAYS.indexOf(`${d.getUTCMonth() + 1}-${d.getUTCDate()}`) > -1) return false;
    return d.getTime() !== easterMonday(d.getUTCFullYear());
  }
  function workday(time) {
    let d = new Date(time);
    while (!isWorkday(d)) d = new Date(d.getTime() + DAY);
    return d;
  }

  class DeliveryEta extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.bound = true;
      this.textEl = this.querySelector('[data-sc-eta-text]');
      this.cutoff = Math.min(23, Math.max(1, Number(this.dataset.cutoff || 14)));
      this.locale = this.dataset.locale || (SC.config && SC.config.locale) || 'it';
      this.render();
      this.timer = setInterval(() => this.render(), 60000);
    }

    disconnectedCallback() {
      clearInterval(this.timer);
      this.bound = false;
    }

    dayLabel(date) {
      const weekday = new Intl.DateTimeFormat(this.locale, { timeZone: 'UTC', weekday: 'long' }).format(date);
      return `${weekday} ${date.getUTCDate()}`;
    }

    countdown(minutesLeft) {
      const h = Math.floor(minutesLeft / 60);
      const m = minutesLeft % 60;
      if (h > 0 && m > 0) return tpl(this.dataset.tplHm, { '[h]': h, '[m]': m });
      if (h > 0) return tpl(this.dataset.tplH, { '[h]': h });
      return tpl(this.dataset.tplMin, { '[m]': Math.max(1, m) });
    }

    render() {
      if (!this.textEl) return;
      try {
        const now = romeNow();
        const today = Date.UTC(now.year, now.month - 1, now.day);
        const minutesLeft = this.cutoff * 60 - (now.hour * 60 + now.minute);
        const openToday = minutesLeft > 0 && isWorkday(new Date(today));
        const ship = openToday ? new Date(today) : workday(today + DAY);
        let delivery = workday(ship.getTime() + DAY);
        delivery = workday(delivery.getTime() + DAY);
        const day = escapeHtml(this.dayLabel(delivery));
        let html;
        if (openToday) {
          html = tpl(this.dataset.tplToday, { '[time]': escapeHtml(this.countdown(minutesLeft)), '[day]': day });
        } else {
          const isTomorrow = ship.getTime() === today + DAY;
          const shipLabel = isTomorrow ? this.dataset.tomorrow || '' : tpl(this.dataset.tplOnDay, { '[day]': this.dayLabel(ship) });
          html = tpl(this.dataset.tplLater, { '[ship]': escapeHtml(shipLabel), '[day]': day });
        }
        if (html !== this.last) {
          this.last = html;
          this.textEl.innerHTML = html;
          tick(this.textEl);
        }
      } catch (e) {
        /* resta il testo di riserva del server */
      }
    }
  }
  SC.define('sc-delivery-eta', DeliveryEta);

  /* ---------- Barra fissa in basso ---------- */
  let stickyCleanup = null;
  function stickyBar() {
    const bar = $('[data-sc-sticky-atc]');
    const anchor = $('[data-sc-buy]');
    if (!bar || !anchor || bar.dataset.bound === '1' || !('IntersectionObserver' in window)) return;
    if (stickyCleanup) stickyCleanup();
    bar.dataset.bound = '1';
    const footer = document.querySelector('.shopify-section-group-footer-group') || document.querySelector('footer');
    let passed = false;
    let atFooter = false;

    const update = () => {
      const show = passed && !atFooter && !SC.isDesktop();
      bar.classList.toggle('is-visible', show);
      bar.setAttribute('aria-hidden', show ? 'false' : 'true');
      if (show) bar.removeAttribute('inert');
      else bar.setAttribute('inert', '');
      document.body.classList.toggle('sc-atc-visible', show);
      // l'altezza vera della barra serve a chi le sta sopra (il pallino "Dubbi?")
      if (show) document.body.style.setProperty('--sc-atc-h', `${bar.offsetHeight}px`);
    };

    const anchorIo = new IntersectionObserver(
      (entries) => {
        // "superato" = il blocco d'acquisto è tutto sopra lo schermo, in qualunque verso si scorra
        passed = entries[0].boundingClientRect.bottom <= 0;
        update();
      },
      { threshold: 0 }
    );
    anchorIo.observe(anchor);

    let footerIo = null;
    if (footer) {
      footerIo = new IntersectionObserver(
        (entries) => {
          atFooter = entries[0].isIntersecting;
          update();
        },
        { threshold: 0 }
      );
      footerIo.observe(footer);
    }

    const onResize = SC.debounce(update, 200);
    window.addEventListener('resize', onResize, { passive: true });
    stickyCleanup = () => {
      anchorIo.disconnect();
      if (footerIo) footerIo.disconnect();
      window.removeEventListener('resize', onResize);
      document.body.classList.remove('sc-atc-visible');
      document.body.style.removeProperty('--sc-atc-h');
    };
  }

  /* ---------- "Avvisami quando torna" ---------- */
  function notifyForm() {
    const panel = $('[data-sc-notify-panel]');
    if (!panel || panel.dataset.bound === '1') return;
    panel.dataset.bound = '1';
    const toggle = $('[data-sc-notify-toggle]');
    const jump = $('[data-sc-notify-jump]');
    const success = panel.querySelector('[data-sc-notify-success]');

    const open = (focusEmail) => {
      panel.hidden = false;
      if (toggle) {
        toggle.setAttribute('aria-expanded', 'true');
        toggle.hidden = true;
      }
      if (focusEmail) {
        const email = panel.querySelector('[data-sc-notify-email]');
        if (email) email.focus({ preventScroll: true });
      }
    };

    if (toggle) toggle.addEventListener('click', () => open(true));

    if (jump) {
      jump.addEventListener('click', () => {
        open(false);
        panel.scrollIntoView({ block: 'center', behavior: reduced() ? 'auto' : 'smooth' });
        setTimeout(() => open(true), 400);
      });
    }

    // ritorno dall'invio riuscito: pannello aperto sulla conferma
    if (success) {
      open(false);
      setTimeout(() => {
        try {
          success.focus({ preventScroll: true });
        } catch (e) {}
        success.scrollIntoView({ block: 'center', behavior: reduced() ? 'auto' : 'smooth' });
      }, 120);
    }
  }

  /* ---------- Colonna info desktop: sticky "intelligente" ----------
     Se la colonna è più alta dello schermo, un top fisso la bloccherebbe con la parte bassa
     (bundle, accordion) fuori vista. Qui il top diventa negativo quanto serve: la colonna scorre
     finché la sua fine è visibile, poi resta ferma mentre la galleria continua. */
  let smartStickyCleanup = null;
  function smartSticky() {
    const info = $('.sc-pdp__info');
    if (!info) return;
    if (smartStickyCleanup) smartStickyCleanup();
    const update = () => {
      if (!SC.isDesktop()) {
        info.style.top = '';
        return;
      }
      const header = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--sc-header-h')) || 76;
      const preferred = header + 20;
      const fit = window.innerHeight - info.offsetHeight - 24;
      info.style.top = `${Math.min(preferred, fit)}px`;
    };
    const onResize = SC.debounce(update, 150);
    const ro = 'ResizeObserver' in window ? new ResizeObserver(onResize) : null;
    if (ro) ro.observe(info);
    window.addEventListener('resize', onResize, { passive: true });
    update();
    smartStickyCleanup = () => {
      if (ro) ro.disconnect();
      window.removeEventListener('resize', onResize);
    };
  }

  /* ---------- avvio ---------- */
  function boot() {
    bindLightbox();
    stickyBar();
    smartSticky();
    notifyForm();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  // editor del tema: la sezione viene ricaricata
  document.addEventListener('shopify:section:load', (e) => {
    if (e.target && e.target.querySelector && e.target.querySelector('[data-sc-pdp]')) boot();
  });
})();
